import { pipe } from 'effect'
import * as A from 'effect/Array'
import { isUndefined } from 'effect/Predicate'
import * as R from 'effect/Record'
import { TFunction } from 'i18next'
import Engine from 'publicodes'
import { useMemo, useState } from 'react'

import {
	ComposantQuestionFournie,
	GroupeDeQuestionsFournies,
} from '@/components/Simulateur/Questions/ComposantQuestionFournie'
import {
	GroupeDeQuestionsPublicodes,
	QuestionPublicodes,
} from '@/domaine/questions'
import { Situation } from '@/domaine/Situation'

const adapteUneQuestionFournie = <S extends Situation>(
	q: ComposantQuestionFournie<S>
): ComposantQuestionFournie<S> => {
	const originalApplicable = q.applicable

	return Object.assign(q, {
		applicable: (situation?: S) =>
			situation === undefined ? false : originalApplicable(situation),
	})
}

const adapteLesQuestionsFourniesDansLeGroupe = <S extends Situation>(
	groupeDeQuestionsFournies: GroupeDeQuestionsFournies<S>
): GroupeDeQuestions<S> => ({
	...groupeDeQuestionsFournies,
	liste: groupeDeQuestionsFournies.liste.map(adapteUneQuestionFournie),
})

const estApplicableDansLaSituation =
	<S extends Situation>(situation: S | undefined) =>
	(question: Question<S>) =>
		question.applicable(situation)

const filtreLesQuestionsApplicablesDansLeGroupe =
	<S extends Situation>(situation: S | undefined) =>
	(groupeDeQuestion: GroupeDeQuestions<S>) => ({
		...groupeDeQuestion,
		liste: groupeDeQuestion.liste.filter(
			estApplicableDansLaSituation(situation)
		),
	})

const filtreLesGroupesSansQuestions = <S extends Situation>(
	groupeDeQuestion: GroupeDeQuestions<S>
) => groupeDeQuestion.liste.length > 0

export type Question<S extends Situation> =
	| ComposantQuestionFournie<S>
	| QuestionPublicodes

export type GroupeDeQuestions<S extends Situation> = {
	titre: (t: TFunction) => string
	réponse?: (engine: Engine, t: TFunction) => string
	Réponse?: React.FunctionComponent
	liste: Array<Question<S>>
}

export interface UseQuestionsProps<S extends Situation = Situation> {
	questionsFourniesPrincipales?: ComposantQuestionFournie<S>[]
	questionsPublicodesPrincipales?: QuestionPublicodes[]
	groupesDeQuestionsFournies?: Record<string, GroupeDeQuestionsFournies<S>>
	groupesDeQuestionsPublicodes?: Record<string, GroupeDeQuestionsPublicodes>
	situation?: S
}

export function useQuestionsÉditorialisées<S extends Situation = Situation>({
	questionsFourniesPrincipales = [],
	questionsPublicodesPrincipales = [],
	groupesDeQuestionsFournies = {},
	groupesDeQuestionsPublicodes = {},
	situation,
}: UseQuestionsProps<S>) {
	const questionsPrincipales = useMemo(
		() =>
			pipe(
				questionsFourniesPrincipales,
				A.map(adapteUneQuestionFournie),
				A.union(questionsPublicodesPrincipales),
				A.filter(estApplicableDansLaSituation(situation))
			),
		[questionsFourniesPrincipales, questionsPublicodesPrincipales, situation]
	)

	const groupesDeQuestions = useMemo(
		() =>
			pipe(
				groupesDeQuestionsFournies,
				R.map(adapteLesQuestionsFourniesDansLeGroupe),
				R.union(groupesDeQuestionsPublicodes, (_, qPublicodes) => qPublicodes),
				R.map(filtreLesQuestionsApplicablesDansLeGroupe(situation)),
				R.filter(filtreLesGroupesSansQuestions)
			),
		[groupesDeQuestionsFournies, groupesDeQuestionsPublicodes, situation]
	)

	const [questionCouranteId, setQuestionCouranteId] = useState<
		string | undefined
	>()
	const questionCourante = isUndefined(questionCouranteId)
		? undefined
		: groupesDeQuestions[questionCouranteId]

	return {
		questionsPrincipales,
		groupesDeQuestions,
		questionCourante,
		setQuestionCouranteId,
	}
}
